// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/AdministrationOverview.jsx

import React from "react";
import TenantReadinessPanel from "./readiness/TenantReadinessPanel";

function MetricCard({ label, value, detail }) {
  return (
    <article className="enterprise-admin-metric-card">
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
      {detail ? <small>{detail}</small> : null}
    </article>
  );
}

export default function AdministrationOverview({
  overview,
  readiness,
  actions,
  saving,
  readinessRefreshedAt,
}) {
  const metrics = overview?.metrics || {};
  const readinessScore = readiness?.score ?? overview?.administration_readiness ?? 0;
  const needsAttention = overview?.needs_attention || [];

  return (
    <section className="enterprise-admin-section">
      <div className="enterprise-admin-section-header">
        <p className="enterprise-admin-eyebrow">Administrative health</p>
        <h2>Is this organization configured, secure, and operational?</h2>
        <p>
          Review enterprise administration readiness, user authority, connector attention,
          and recent administrative change activity.
        </p>
      </div>

      <TenantReadinessPanel
        readiness={readiness || overview?.readiness}
        score={readinessScore}
        onRefresh={actions?.refreshTenantReadiness}
        saving={saving}
        refreshedAt={readinessRefreshedAt}
      />

      <div className="enterprise-admin-metrics-grid">
        <MetricCard label="Active users" value={metrics.active_users} />
        <MetricCard label="Pending invitations" value={metrics.pending_invitations} />
        <MetricCard label="Privileged administrators" value={metrics.privileged_administrators} />
        <MetricCard label="Roles configured" value={metrics.roles_configured} />
        <MetricCard label="Connected systems" value={metrics.connected_systems} />
        <MetricCard label="Connector attention" value={metrics.connectors_requiring_attention} />
        <MetricCard label="MFA coverage" value={metrics.mfa_coverage ?? "Pending"} />
        <MetricCard label="Recent admin changes" value={metrics.recent_administrative_changes} detail="Last 7 days" />
      </div>

      <div className="enterprise-admin-attention-card">
        <h3>Needs attention</h3>
        {needsAttention.length ? (
          <ul>
            {needsAttention.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : (
          <p>No critical administrative setup actions detected.</p>
        )}
      </div>
    </section>
  );
}
